import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type Farm, type Master, type MasterCreateOut } from "../api";

/** Farms page — mỗi farm 1 card, masters nested bên dưới. Gộp từ hai
 *  trang cũ (Farms + Masters) để operator thấy luôn quan hệ farm → masters
 *  mà không phải cross-reference. "Register Master" nằm trong context
 *  từng farm card, không cần dropdown chọn farm. */
export function FarmsPage() {
  const [farms, setFarms] = useState<Farm[] | null>(null);
  const [masters, setMasters] = useState<Master[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateFarm, setShowCreateFarm] = useState(false);
  const [createMasterForFarm, setCreateMasterForFarm] = useState<Farm | null>(null);
  const [justCreated, setJustCreated] = useState<MasterCreateOut | null>(null);

  async function refresh() {
    try {
      const [f, m] = await Promise.all([
        api.get<Farm[]>("/farms"),
        api.get<Master[]>("/masters"),
      ]);
      setFarms(f);
      setMasters(m);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { refresh(); }, []);

  // Pre-group masters by farm_id so each card only scans its own subset.
  // Cheap for < a few hundred masters; upgrade to Map if we scale beyond.
  const mastersByFarm = useMemo(() => {
    const m = new Map<string, Master[]>();
    if (!masters) return m;
    for (const row of masters) {
      const list = m.get(row.farm_id);
      if (list) list.push(row);
      else m.set(row.farm_id, [row]);
    }
    return m;
  }, [masters]);

  return (
    <>
      <div className="row" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Farms</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreateFarm(true)}>
          + Tạo farm
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}

      {farms === null && <div className="muted">Đang tải…</div>}
      {farms?.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Chưa có farm nào. Bấm "Tạo farm" để tạo farm đầu tiên + đăng ký master.
          </p>
        </div>
      )}

      {farms?.map((farm) => (
        <FarmCard
          key={farm.id}
          farm={farm}
          masters={mastersByFarm.get(farm.id) ?? []}
          onRegisterMaster={() => setCreateMasterForFarm(farm)}
          onMasterChanged={refresh}
        />
      ))}

      {showCreateFarm && (
        <CreateFarmModal
          onClose={() => setShowCreateFarm(false)}
          onCreated={() => { setShowCreateFarm(false); refresh(); }}
        />
      )}

      {createMasterForFarm && (
        <CreateMasterModal
          farm={createMasterForFarm}
          onClose={() => setCreateMasterForFarm(null)}
          onCreated={(m) => {
            setCreateMasterForFarm(null);
            setJustCreated(m);
            refresh();
          }}
        />
      )}

      {justCreated && (
        <MasterCreatedModal master={justCreated} onClose={() => setJustCreated(null)} />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Farm card — shows farm metadata + nested master list.
// ═══════════════════════════════════════════════════════════════════

function FarmCard({
  farm, masters, onRegisterMaster, onMasterChanged,
}: {
  farm: Farm;
  masters: Master[];
  onRegisterMaster: () => void;
  onMasterChanged: () => void;
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
        <div className="row" style={{ gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "1.05rem", fontWeight: 600 }}>
              {farm.name || farm.slug}
            </div>
            <div className="muted" style={{ fontSize: "0.85rem", marginTop: 2 }}>
              <code className="inline">{farm.slug}</code>
              {" · "}
              {farm.timezone}
              {farm.lat !== null && farm.lon !== null && (
                <> {" · "} {farm.lat.toFixed(4)}, {farm.lon.toFixed(4)}</>
              )}
            </div>
            {farm.notes && (
              <div className="muted" style={{ fontSize: "0.85rem", marginTop: 6 }}>
                {farm.notes}
              </div>
            )}
          </div>
          <Link to={`/farms/${farm.id}/operate`}>
            <button className="primary">Vận hành →</button>
          </Link>
          <button onClick={onRegisterMaster}>+ Đăng ký Master</button>
        </div>
      </div>

      <div>
        {masters.length === 0 ? (
          <div style={{ padding: "16px 20px" }} className="muted">
            Chưa có master nào cho farm này. Bấm "Đăng ký Master" để thêm thiết bị.
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>MQTT username</th>
                  <th>MAC</th>
                  <th>Trạng thái</th>
                  <th>Last seen</th>
                  <th style={{ textAlign: "right" }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {masters.map((m) => (
                  <MasterRow key={m.id} master={m} onChanged={onMasterChanged} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MasterRow({ master, onChanged }: { master: Master; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // "Online" = heartbeat within the last 5 min. Matches Dashboard's tile so
  // the two screens don't disagree about what online means.
  const isOnline = !master.disabled_at && master.last_seen_at
    && Date.now() - new Date(master.last_seen_at).getTime() < 5 * 60 * 1000;

  async function toggleDisabled() {
    const label = master.disabled_at ? "kích hoạt lại" : "vô hiệu hoá";
    if (!window.confirm(`${label[0].toUpperCase() + label.slice(1)} master ${master.label}?`)) return;
    setBusy("toggle"); setError(null);
    try {
      await api.put(`/masters/${master.id}`, { disabled: !master.disabled_at });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onDelete() {
    if (!window.confirm(
      `Xoá master ${master.label}? Bridge config trên farm sẽ hết hiệu lực — ` +
      `farm phải đăng ký lại nếu muốn kết nối cloud.`,
    )) return;
    setBusy("delete"); setError(null);
    try {
      await api.del(`/masters/${master.id}`);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <tr>
      <td>
        <strong>{master.label || "—"}</strong>
        {error && <div className="muted" style={{ color: "var(--danger)", fontSize: "0.75rem" }}>{error}</div>}
      </td>
      <td className="muted"><code className="inline">{master.mqtt_username}</code></td>
      <td className="muted"><code className="inline">{master.hardware_mac || "—"}</code></td>
      <td>
        {master.disabled_at ? (
          <span className="badge disabled">disabled</span>
        ) : isOnline ? (
          <span className="badge owner">online</span>
        ) : (
          <span className="badge" style={{ background: "#fef3c7", color: "#92400e" }}>offline</span>
        )}
      </td>
      <td className="muted" style={{ fontSize: "0.8rem" }}>
        {master.last_seen_at ? new Date(master.last_seen_at).toLocaleString() : "chưa có"}
      </td>
      <td style={{ textAlign: "right" }}>
        <div style={{ display: "inline-flex", gap: 6 }}>
          <button
            onClick={toggleDisabled}
            disabled={!!busy}
            style={{ fontSize: "0.8rem", padding: "4px 10px" }}
          >
            {busy === "toggle" ? "…" : master.disabled_at ? "Kích hoạt" : "Disable"}
          </button>
          <button
            className="danger"
            onClick={onDelete}
            disabled={!!busy}
            style={{ fontSize: "0.8rem", padding: "4px 10px" }}
          >
            {busy === "delete" ? "…" : "Xoá"}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Create farm
// ═══════════════════════════════════════════════════════════════════

function CreateFarmModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Asia/Ho_Chi_Minh");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post<Farm>("/farms", {
        slug, name, timezone,
        lat: lat ? parseFloat(lat) : null,
        lon: lon ? parseFloat(lon) : null,
        notes,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Tạo farm thất bại");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Tạo farm mới</h2>
        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}
          <div className="form-row">
            <div className="field">
              <label>Slug (URL-safe, a-z 0-9 -)</label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="orton-bore-1"
                pattern="[a-z0-9-]+"
                required
              />
            </div>
            <div className="field">
              <label>Tên hiển thị</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Orton Road Bore 1" required />
            </div>
          </div>
          <div className="field">
            <label>Timezone (IANA)</label>
            <input value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="field">
              <label>Latitude (tuỳ chọn)</label>
              <input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="10.762622" />
            </div>
            <div className="field">
              <label>Longitude (tuỳ chọn)</label>
              <input value={lon} onChange={(e) => setLon(e.target.value)} placeholder="106.660172" />
            </div>
          </div>
          <div className="field">
            <label>Ghi chú</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="btn-row">
            <button type="button" onClick={onClose}>Huỷ</button>
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Đang tạo…" : "Tạo farm"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Register master (scoped to a specific farm)
// ═══════════════════════════════════════════════════════════════════

function CreateMasterModal({
  farm, onClose, onCreated,
}: {
  farm: Farm;
  onClose: () => void;
  onCreated: (m: MasterCreateOut) => void;
}) {
  const [label, setLabel] = useState("");
  const [mac, setMac] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const m = await api.post<MasterCreateOut>("/masters", {
        farm_id: farm.id,
        label,
        hardware_mac: mac,
      });
      onCreated(m);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Đăng ký thất bại");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Đăng ký master mới</h2>
        <p className="muted" style={{ fontSize: "0.85rem", marginTop: 0 }}>
          Thêm vào farm <strong>{farm.name}</strong> ({farm.slug}).
        </p>
        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}
          <div className="field">
            <label>Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Raspberry Pi #1 — nhà kho"
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label>Hardware MAC (tuỳ chọn)</label>
            <input
              value={mac}
              onChange={(e) => setMac(e.target.value)}
              placeholder="B8:27:EB:1A:2B:3C"
            />
          </div>
          <p className="muted" style={{ fontSize: "0.85rem" }}>
            API key và bridge config sẽ hiển thị <strong>1 lần duy nhất</strong> sau khi tạo.
            Copy ngay vào password manager hoặc download file.
          </p>
          <div className="btn-row">
            <button type="button" onClick={onClose}>Huỷ</button>
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Đang tạo…" : "Tạo master"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** One-shot display of api_key after a master is created.
 *
 *  Chỉ 1 cách duy nhất: enrollment qua master_frontend (tab Cloud).
 *  Bridge config + manual SSH instructions không hiển thị — master_frontend
 *  gọi /api/enroll/bundle tự fetch + ghi file. Operator copy 1 chuỗi
 *  `portal#key`, dán vào master_frontend, xong.
 *
 *  Plaintext api_key không bao giờ rời dialog này — chỉ bcrypt hash được
 *  lưu. Rotation sinh key mới. */
function MasterCreatedModal({
  master, onClose,
}: { master: MasterCreateOut; onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);

  const portalOrigin = window.location.origin;

  // Combined enrollment string — dán 1 lần vào master_frontend để cả
  // portal URL + api_key tự tách sang 2 field. Separator '#' reserved
  // trong URL → không escape khi gửi qua Zalo/SMS.
  const enrollmentString = `${portalOrigin}#${master.api_key}`;

  async function copy(what: string, value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 620 }}>
        <h2>✅ Master đã được tạo</h2>

        <div className="alert warn">
          <strong>Lưu ngay:</strong> API key chỉ hiển thị lần này. Đóng dialog
          là mất — phải rotate nếu cần lại.
        </div>

        <div className="field">
          <label>Chuỗi enrollment (copy 1 lần, gửi cho chủ farm)</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              readOnly
              value={enrollmentString}
              style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
            />
            <button
              type="button"
              onClick={() => copy("combined", enrollmentString)}
              className={copied === "combined" ? "primary" : ""}
            >
              {copied === "combined" ? "✓ Đã copy" : "Copy"}
            </button>
          </div>
        </div>

        <div className="alert ok" style={{ fontSize: "0.9rem" }}>
          <strong>Cách dùng trên farm</strong> (chủ farm làm, không cần SSH):<br /><br />
          1. Mở master_frontend trên Pi: <code className="inline">http://&lt;pi-ip&gt;:8880</code><br />
          2. Vào <strong>Settings → tab Cloud</strong><br />
          3. Paste chuỗi trên vào ô <em>"Dán chuỗi enrollment"</em><br />
          4. Bấm <strong>Enroll</strong>. Master_backend tự download CA + ghi bridge config + restart mosquitto.
        </div>

        <details style={{ marginTop: 12 }}>
          <summary className="muted" style={{ fontSize: "0.85rem", cursor: "pointer" }}>
            Xem từng phần (Cloud URL + API Key riêng)
          </summary>
          <div style={{ marginTop: 12 }}>
            <div className="field">
              <label>Cloud Portal URL</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={portalOrigin} style={{ fontFamily: "monospace" }} />
                <button
                  type="button"
                  onClick={() => copy("portal", portalOrigin)}
                  className={copied === "portal" ? "primary" : ""}
                >
                  {copied === "portal" ? "✓" : "Copy"}
                </button>
              </div>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>API Key</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={master.api_key} style={{ fontFamily: "monospace" }} />
                <button
                  type="button"
                  onClick={() => copy("key", master.api_key)}
                  className={copied === "key" ? "primary" : ""}
                >
                  {copied === "key" ? "✓" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </details>

        <div className="btn-row">
          <button type="button" className="primary" onClick={onClose}>
            Đã lưu, đóng lại
          </button>
        </div>
      </div>
    </div>
  );
}
